import { inject, injectable } from 'inversify';
import DependencyIdentifiers from '@/ioc/DependencyIdentifiers';
import { auth, patient as store } from '@/store/modules';
import { PatientModel } from '@/models/patient';
import { IApiService, IPatientService } from '@/interfaces/Services';

export type PatientResponse = {
  data: PatientModel
};

export type PagedPatientResponse = {
  data: PatientModel[],
  meta: { total: number; }
};

@injectable()
export default class PatientService implements IPatientService {
  private _api: IApiService;

  constructor(@inject(DependencyIdentifiers.ApiService) api: IApiService) {
    this._api = api;
  }

  async getMany(page: number = 1, size: number = 20): Promise<[PatientModel[], number]> {
    const res = await this._api
      .get<PagedPatientResponse>(`/patients?size=${size}&page=${page}&filter[centerId]=${auth.currentCenterId}`);
    return [res.data, res.meta.total];
  }

  async getById(id: string): Promise<PatientModel> {
    const patient = await this._api
      .get<{ data: PatientModel }>(`/patients/${id}`)
      .then(res => res.data);
    store.cacheOne(patient);
    return patient;
  }

  async create(data: Partial<PatientModel>): Promise<PatientModel> {
    const res = await this._api
      .post<PatientResponse>(`/patients`, { data });
    store.cacheOne(res.data);
    return res.data;
  }

  async updateById(id: string, data: Partial<PatientModel>): Promise<PatientModel> {
    const res = await this._api
      .patch<PatientResponse>(`/patients/${id}`, { data });
    store.cacheOne(res.data);
    return res.data;
  }

  async reset(id: string): Promise<void> {
    await this._api.post(`/patients/${id}/reset`, {});
  }

  async recovery(id: string): Promise<string> {
    return await this._api.get(`/patients/${id}/recovery`)
      .then((res: any) => res.data.recoveryKey as string);
  }
}
